import { FC, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Button } from '@/components';
import { ButtonColor, ButtonSize, Icons } from '@/enums';
import strings from '@/language/Swedish';
import { ResidenceType } from '@/api/mutations/useRegistrySearch';
import { registryColumns } from './registryConfig';
import RegistryColumnDialog from './RegistryColumnDialog';
import {
  exportResidencesToCsv,
  exportResidencesToExcel,
} from './exportResidences';

const TableContainer = styled.div<{ $expanded: boolean }>`
  ${tw`flex flex-col rounded-xl border border-white bg-white/80 backdrop-blur-xl shadow-2xl overflow-hidden`};

  ${({ $expanded }) => $expanded && tw`flex-1 min-h-0`};
`;

const TableHeading = styled.div`
  ${tw`flex items-center justify-between gap-2 px-4 py-2`};
`;

const Title = styled.span`
  ${tw`flex-1 text-sm font-medium text-slate-800`};
`;

const ActionContainer = styled.div`
  ${tw`flex items-center gap-2`};
`;

const ScrollWrapper = styled.div<{ $expanded: boolean }>`
  ${tw`overflow-x-auto overflow-y-auto`};

  ${({ $expanded }) => ($expanded ? tw`flex-1` : tw`max-h-80`)};
`;

const StyledTable = styled.table`
  ${tw`w-full text-xs text-left text-slate-600 whitespace-nowrap`};

  th {
    ${tw`sticky top-0 px-3 py-2 font-medium text-slate-800 bg-slate-100/95 backdrop-blur`};
  }

  td {
    ${tw`px-3 py-1.5 border-t border-slate-800/5`};
  }

  tbody tr:hover {
    ${tw`bg-slate-800/5`};
  }
`;

const STORAGE_KEY = 'vg-registry-table-columns';

const defaultVisibleColumns = (): Record<string, boolean> =>
  Object.fromEntries(
    registryColumns.map((column) => [column.name, column.priority !== 'low'])
  );

const loadVisibleColumns = (): Record<string, boolean> => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return { ...defaultVisibleColumns(), ...JSON.parse(saved) };
  } catch {
    // Corrupt or unavailable storage falls back to defaults
  }
  return defaultVisibleColumns();
};

interface Props {
  residences: ResidenceType[];
  availableColumns?: typeof registryColumns;
  expanded?: boolean;
}

const ResidenceTable: FC<Props> = ({
  residences,
  availableColumns = registryColumns,
  expanded = false,
}) => {
  const [visibleColumns, setVisibleColumns] =
    useState<Record<string, boolean>>(loadVisibleColumns);

  if (!residences.length) return null;

  const toggleColumn = (name: string) => {
    setVisibleColumns((previous) => {
      const next = { ...previous, [name]: !previous[name] };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Ignore unavailable storage; selection still applies this session
      }
      return next;
    });
  };

  const columns = availableColumns.filter(
    (column) => visibleColumns[column.name]
  );

  return (
    <TableContainer $expanded={expanded}>
      <TableHeading>
        <Title>
          {strings.registry.results} ({residences.length})
        </Title>
        <ActionContainer>
          <Button
            color={ButtonColor.border}
            size={ButtonSize.medium}
            icon={Icons.excel}
            onClick={() =>
              exportResidencesToExcel(
                residences,
                columns,
                strings.registry.exportFileName
              )
            }
          >
            {strings.registry.exportExcel}
          </Button>
          <Button
            color={ButtonColor.border}
            size={ButtonSize.medium}
            icon={Icons.excel}
            onClick={() =>
              exportResidencesToCsv(
                residences,
                columns,
                strings.registry.exportFileName
              )
            }
          >
            {strings.registry.exportCsv}
          </Button>
          <RegistryColumnDialog
            columns={availableColumns}
            visibleColumns={visibleColumns}
            toggleColumn={toggleColumn}
          />
        </ActionContainer>
      </TableHeading>
      <ScrollWrapper $expanded={expanded}>
        <StyledTable>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.name}>{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {residences.map((residence) => (
              <tr key={residence.id}>
                {columns.map((column) => (
                  <td key={column.name}>{residence[column.name] ?? '-'}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </StyledTable>
      </ScrollWrapper>
    </TableContainer>
  );
};

export default ResidenceTable;
